import {
  Checkbox,
  FormControlLabel,
  Grid,
  ListSubheader,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Loading from 'components/Loading/Loading';
import { COMPETENCY_CARD_TYPES } from 'constants/competency-card-types';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const CUSTOM_TYPE = 'CUSTOM_BUSINESS_COMPETENCY';

const useStyles = makeStyles((theme) => ({
  infoText: {
    paddingBottom: theme.spacing(1),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  subheader: {
    paddingLeft: 0,
    lineHeight: '32px',
  },
  emptyState: {
    padding: theme.spacing(1, 0),
  },
}));

export const RequiredCompetencyCardsContent = ({
  loading,
  isLoadingCompetencyTypes,
  competencyTypes,
  selectedRequiredCardTypes,
  onToggleRequiredCardType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const standardOptions = useMemo(
    () =>
      Object.values(COMPETENCY_CARD_TYPES)
        .map((option) => ({
          value: option.type,
          label: t(`cards-and-competencies.${option.name}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [t]
  );

  const customOptions = useMemo(() => {
    const seen = new Set();
    const unique = [];

    (competencyTypes || [])
      .filter(
        (competencyType) =>
          !competencyType.deletedAt && competencyType.name?.toString().trim()
      )
      .forEach((competencyType) => {
        const name = competencyType.name.toString().trim();
        const key = name.toLowerCase();
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        unique.push({ value: `${CUSTOM_TYPE}#${name}`, label: name });
      });

    return unique.sort((a, b) => a.label.localeCompare(b.label));
  }, [competencyTypes]);

  const selectedSet = useMemo(
    () => new Set(selectedRequiredCardTypes || []),
    [selectedRequiredCardTypes]
  );

  const renderOption = (option) => (
    <Grid item xs={12} sm={6} key={option.value}>
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedSet.has(option.value)}
            onChange={(e) =>
              onToggleRequiredCardType(option.value, e.target.checked)
            }
            disabled={loading}
            color="secondary"
          />
        }
        label={option.label}
      />
    </Grid>
  );

  return (
    <>
      <Grid item xs={12}>
        <Typography className={classes.infoText}>
          {t('project_dialog.required_competency_cards_info')}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <ListSubheader disableSticky className={classes.subheader}>
          {t('project_dialog.required_competency_cards_general_subheader')}
        </ListSubheader>
      </Grid>

      <Grid container item spacing={0}>
        {standardOptions.map(renderOption)}
      </Grid>

      <Grid item xs={12}>
        <ListSubheader disableSticky className={classes.subheader}>
          {t('project_dialog.required_competency_cards_employer_subheader')}
        </ListSubheader>
      </Grid>

      {isLoadingCompetencyTypes ? (
        <Grid item xs={12}>
          <Loading tiny />
        </Grid>
      ) : customOptions.length > 0 ? (
        <Grid container item spacing={0}>
          {customOptions.map(renderOption)}
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Typography className={classes.emptyState}>
            {t('common.no_options')}
          </Typography>
        </Grid>
      )}
    </>
  );
};

RequiredCompetencyCardsContent.propTypes = {
  loading: PropTypes.bool,
  isLoadingCompetencyTypes: PropTypes.bool,
  competencyTypes: PropTypes.array,
  selectedRequiredCardTypes: PropTypes.array,
  onToggleRequiredCardType: PropTypes.func,
};

RequiredCompetencyCardsContent.defaultProps = {
  loading: false,
  isLoadingCompetencyTypes: false,
  competencyTypes: [],
  selectedRequiredCardTypes: [],
  onToggleRequiredCardType: () => {},
};
